import { useSelector } from 'react-redux';

import { ListIcon } from '@frontend/legacy-components';
import {
  TableIcon,
  WrenchIcon,
  MaterialShapeRectBarIcon,
  DnaWarningIcon,
  DollarSignIcon,
  PurchasedComponentIcon,
  MultipleWorkflowStatusIcon,
  ReqsReviewFeatureIcon,
} from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import {
  hasProcessPermission,
  hasQuotingPermission,
} from 'modules/common/permissions';

import { useFeatureFlagValue } from 'store/session/hooks';
import { getIsDiscountsEnabled } from 'store/session/selectors';

import ConfigurePageNavTabData from './types/ConfigurePageNavTabData.interface';
import ConfigurePageNavTabRequirement from './types/ConfigurePageNavTabRequirement.enum';

const useTabsData = () => {
  const isProcessEnabled = useSelector(hasProcessPermission);
  const isQuotingEnabled = useSelector(hasQuotingPermission);
  return [
    {
      name: 'Processes',
      to: '/processes',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: ListIcon,
    },
    {
      name: 'Operations',
      to: '/processes/operations',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: WrenchIcon,
    },
    {
      name: 'Materials',
      to: '/processes/materials',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      icon: MaterialShapeRectBarIcon,
    },
    {
      name: 'Pricing',
      to: '/processes/pricing',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: DollarSignIcon,
    },
    {
      name: 'Discounts',
      to: '/processes/discounts',
      requirements: [
        ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE,
        ConfigurePageNavTabRequirement.DISCOUNTS_ENABLED,
      ],
      isDisabled: !isProcessEnabled,
      icon: DollarSignIcon,
    },
    {
      name: 'Add-Ons',
      to: '/processes/add_ons',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: DollarSignIcon,
    },
    {
      name: 'Purchased Components',
      to: '/processes/components',
      requirements: [
        ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE,
        ConfigurePageNavTabRequirement.ASSEMBLIES_ENABLED,
      ],
      isDisabled: !isQuotingEnabled,
      icon: PurchasedComponentIcon,
    },
    {
      name: 'Tables',
      to: '/processes/tables',
      requirements: [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: TableIcon,
    },
    {
      name: 'Interrogations',
      to: '/processes/interrogations',
      isDisabled: !isProcessEnabled,
      icon: DnaWarningIcon,
    },
    {
      name: 'Workflows',
      to: '/processes/workflows',
      requirements: [ConfigurePageNavTabRequirement.WORKFLOWS_AVAILABLE],
      isDisabled: !isProcessEnabled,
      icon: MultipleWorkflowStatusIcon,
    },
    {
      name: 'Review Rules',
      to: '/processes/rules',
      requirements: [ConfigurePageNavTabRequirement.REQS_REVIEW_ENABLED],
      isDisabled: !isProcessEnabled,
      icon: ReqsReviewFeatureIcon,
    },
  ];
};

export const useTabsMeetingRequirements = () => {
  const isAssembliesEnabled = useFeatureFlagValue(
    featureFlagKeys.isAssembliesEnabled,
  );
  const isQuotingToolAvailable = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );
  const isWorkflowsAvailable = useFeatureFlagValue(
    featureFlagKeys.isWorkflowsAvailable,
  );
  const isReqsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );
  const isDiscountsEnabled = useSelector(getIsDiscountsEnabled);
  const requirementsToIsMetMap: Record<
    ConfigurePageNavTabRequirement,
    boolean
  > = {
    [ConfigurePageNavTabRequirement.ASSEMBLIES_ENABLED]: !!isAssembliesEnabled,
    [ConfigurePageNavTabRequirement.QUOTING_TOOL_AVAILABLE]:
      !!isQuotingToolAvailable,
    [ConfigurePageNavTabRequirement.WORKFLOWS_AVAILABLE]:
      !!isWorkflowsAvailable,
    [ConfigurePageNavTabRequirement.DISCOUNTS_ENABLED]: !!isDiscountsEnabled,
    [ConfigurePageNavTabRequirement.REQS_REVIEW_ENABLED]: !!isReqsReviewEnabled,
  };

  const TABS_DATA: ConfigurePageNavTabData[] = useTabsData();
  return TABS_DATA.filter(({ requirements = [] }) =>
    requirements.reduce(
      (werePreviousRequirementsMet, currentRequirement) =>
        werePreviousRequirementsMet &&
        requirementsToIsMetMap[currentRequirement],
      true,
    ),
  );
};
